import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject, HostListener, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { Subject, debounceTime, distinctUntilChanged, takeUntil, switchMap, of, catchError } from 'rxjs';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  SearchEntityType,
  SearchSuggestion,
  RecentSearch,
  SearchCriteria
} from '../../models/file-vault.models';

export interface SearchBarAction {
  action: 'search' | 'select-suggestion' | 'select-recent' | 'clear-recent';
  query?: string;
  entityType?: SearchEntityType;
  suggestion?: SearchSuggestion;
  recentSearch?: RecentSearch;
}

@Component({
  selector: 'fv-search-bar',
  standalone: true,
  template: `
    <div class="search-bar-container" #searchContainer>
      <div class="search-input-group">
        <!-- Entity Type Filter -->
        <kendo-dropdownlist
          *ngIf="showEntityFilter"
          [(ngModel)]="selectedEntityType"
          [data]="entityTypes"
          textField="text"
          valueField="value"
          [valuePrimitive]="true"
          (valueChange)="onEntityTypeChange($event)"
          class="entity-filter">
        </kendo-dropdownlist>

        <!-- Search Input -->
        <div class="search-input-wrapper">
          <input kendoTextBox
                 #searchInput
                 [(ngModel)]="searchQuery"
                 [placeholder]="placeholder"
                 class="search-input"
                 (input)="onSearchInput()"
                 (focus)="onInputFocus()"
                 (keydown)="onKeyDown($event)" />

          <button kendoButton
                  *ngIf="searchQuery"
                  [look]="'flat'"
                  class="clear-button"
                  (click)="clearSearch()"
                  title="Clear search">
            ✕
          </button>
        </div>

        <!-- Search Button -->
        <button kendoButton
                [themeColor]="'primary'"
                (click)="performSearch()"
                [disabled]="!searchQuery.trim()">
          Search
        </button>
      </div>

      <!-- Dropdown Panel (Suggestions + Recent) -->
      <div class="dropdown-panel" *ngIf="showDropdown && (suggestions.length > 0 || recentSearches.length > 0)">
        <!-- Suggestions -->
        <div class="dropdown-section" *ngIf="suggestions.length > 0">
          <div class="section-header">Suggestions</div>
          <div class="suggestion-item"
               *ngFor="let suggestion of suggestions; let i = index"
               [class.highlighted]="highlightedIndex === i"
               (click)="selectSuggestion(suggestion)"
               (mouseenter)="highlightedIndex = i">
            <span class="suggestion-icon" [ngClass]="getEntityTypeClass(suggestion.entityType)">
              {{ getEntityTypeIcon(suggestion.entityType) }}
            </span>
            <span class="suggestion-text">{{ suggestion.text }}</span>
            <span class="suggestion-category">{{ suggestion.category }}</span>
            <span class="suggestion-count" *ngIf="suggestion.matchCount > 1">
              ({{ suggestion.matchCount }})
            </span>
          </div>
        </div>

        <!-- Recent Searches -->
        <div class="dropdown-section" *ngIf="recentSearches.length > 0 && !searchQuery">
          <div class="section-header">
            Recent Searches
            <button kendoButton [look]="'flat'" class="clear-recent" (click)="clearRecentSearches($event)">
              Clear
            </button>
          </div>
          <div class="recent-item"
               *ngFor="let recent of recentSearches; let i = index"
               [class.highlighted]="highlightedIndex === suggestions.length + i"
               (click)="selectRecentSearch(recent)"
               (mouseenter)="highlightedIndex = suggestions.length + i">
            <span class="recent-icon">⏱</span>
            <span class="recent-query">{{ recent.query }}</span>
            <span class="recent-type" *ngIf="recent.entityType !== SearchEntityType.All">
              {{ getEntityTypeLabel(recent.entityType) }}
            </span>
            <span class="recent-count">{{ recent.resultCount }} results</span>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .search-bar-container {
      position: relative;
      width: 100%;
    }

    .search-input-group {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .entity-filter {
      width: 120px;
      flex-shrink: 0;
    }

    .search-input-wrapper {
      flex: 1;
      position: relative;
      display: flex;
      align-items: center;
    }

    .search-input {
      width: 100%;
      padding-right: 30px;
    }

    .clear-button {
      position: absolute;
      right: 4px;
      min-width: 24px;
      height: 24px;
      padding: 0;
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .clear-button:hover {
      color: var(--fv-text);
    }

    .dropdown-panel {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: var(--fv-surface);
      border: 1px solid var(--fv-border);
      border-radius: 4px;
      box-shadow: 0 4px 12px var(--fv-shadow);
      z-index: 1000;
      max-height: 400px;
      overflow-y: auto;
      margin-top: 4px;
    }

    .dropdown-section {
      padding: 8px 0;
    }

    .dropdown-section:not(:last-child) {
      border-bottom: 1px solid var(--fv-border);
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 12px 8px;
      font-size: 11px;
      font-weight: 600;
      color: var(--fv-text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .clear-recent {
      font-size: 11px;
      text-transform: none;
      letter-spacing: 0;
    }

    .suggestion-item, .recent-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      cursor: pointer;
      transition: background-color 0.15s;
    }

    .suggestion-item:hover, .recent-item:hover,
    .suggestion-item.highlighted, .recent-item.highlighted {
      background: var(--fv-surface-hover);
    }

    .suggestion-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
    }

    .suggestion-icon.part { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .suggestion-icon.document { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .suggestion-icon.file { background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }
    .suggestion-icon.change { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .suggestion-icon.all { background: var(--fv-status-bluegrey-bg); color: var(--fv-status-bluegrey-fg); }

    .suggestion-text {
      flex: 1;
      font-weight: 500;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .suggestion-category {
      font-size: 12px;
      color: var(--fv-text-muted);
    }

    .suggestion-count {
      font-size: 11px;
      color: var(--fv-text-secondary);
      background: var(--fv-border-subtle);
      padding: 2px 6px;
      border-radius: 10px;
    }

    .recent-icon {
      color: var(--fv-text-muted);
      font-size: 14px;
    }

    .recent-query {
      flex: 1;
      color: var(--fv-text);
    }

    .recent-type {
      font-size: 11px;
      padding: 2px 6px;
      border-radius: 4px;
      background: var(--fv-status-indigo-bg);
      color: var(--fv-status-indigo-fg);
    }

    .recent-count {
      font-size: 11px;
      color: var(--fv-text-muted);
    }
  `],
  imports: [CommonModule, FormsModule, InputsModule, ButtonsModule, DropDownsModule]
})
export class SearchBarComponent implements OnInit, OnDestroy {
  private api = inject(FileVaultApiService);
  private elementRef = inject(ElementRef);
  private destroy$ = new Subject<void>();
  private searchInput$ = new Subject<string>();

  @ViewChild('searchInput') searchInputElement!: ElementRef;

  /** Placeholder text for the search input */
  @Input() placeholder = 'Search parts, documents, files, changes...';

  /** Show entity type filter dropdown */
  @Input() showEntityFilter = true;

  /** Initial entity type filter */
  @Input() initialEntityType: SearchEntityType = SearchEntityType.All;

  /** Debounce time for autocomplete in ms */
  @Input() debounceTime = 300;

  /** Maximum number of suggestions to show */
  @Input() maxSuggestions = 8;

  /** Maximum number of recent searches to show */
  @Input() maxRecentSearches = 5;

  /** Emitted when user performs a search action */
  @Output() searchAction = new EventEmitter<SearchBarAction>();

  /** Emitted when search criteria changes (for real-time search) */
  @Output() searchCriteriaChange = new EventEmitter<SearchCriteria>();

  // Expose enum to template
  SearchEntityType = SearchEntityType;

  searchQuery = '';
  selectedEntityType: SearchEntityType = SearchEntityType.All;
  suggestions: SearchSuggestion[] = [];
  recentSearches: RecentSearch[] = [];
  showDropdown = false;
  highlightedIndex = -1;

  entityTypes = [
    { text: 'All', value: SearchEntityType.All },
    { text: 'Parts', value: SearchEntityType.Part },
    { text: 'Documents', value: SearchEntityType.Document },
    { text: 'Files', value: SearchEntityType.File },
    { text: 'Changes', value: SearchEntityType.Change }
  ];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showDropdown = false;
    }
  }

  ngOnInit(): void {
    this.selectedEntityType = this.initialEntityType;
    this.loadRecentSearches();
    this.setupSearchInput();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchInput(): void {
    this.searchInput$.pipe(
      debounceTime(this.debounceTime),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query || query.length < 2) {
          return of([]);
        }
        return this.api.getSearchSuggestions(query, this.selectedEntityType, this.maxSuggestions).pipe(
          catchError(() => of([]))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(suggestions => {
      this.suggestions = suggestions;
      this.highlightedIndex = -1;
    });
  }

  private async loadRecentSearches(): Promise<void> {
    try {
      const recent = await this.api.getRecentSearches().toPromise();
      this.recentSearches = (recent ?? []).slice(0, this.maxRecentSearches);
    } catch {
      this.recentSearches = [];
    }
  }

  onSearchInput(): void {
    this.searchInput$.next(this.searchQuery);
    this.showDropdown = true;
  }

  onInputFocus(): void {
    this.showDropdown = true;
    if (!this.searchQuery) {
      this.loadRecentSearches();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    const totalItems = this.suggestions.length + (this.searchQuery ? 0 : this.recentSearches.length);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.highlightedIndex < totalItems - 1) {
          this.highlightedIndex++;
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.highlightedIndex > 0) {
          this.highlightedIndex--;
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.suggestions.length) {
          this.selectSuggestion(this.suggestions[this.highlightedIndex]);
        } else if (this.highlightedIndex >= this.suggestions.length) {
          const recentIndex = this.highlightedIndex - this.suggestions.length;
          if (recentIndex < this.recentSearches.length) {
            this.selectRecentSearch(this.recentSearches[recentIndex]);
          }
        } else {
          this.performSearch();
        }
        break;
      case 'Escape':
        this.showDropdown = false;
        this.highlightedIndex = -1;
        break;
    }
  }

  onEntityTypeChange(entityType: SearchEntityType): void {
    this.selectedEntityType = entityType;
    if (this.searchQuery) {
      this.onSearchInput();
    }
  }

  performSearch(): void {
    if (!this.searchQuery.trim()) return;

    this.showDropdown = false;
    this.highlightedIndex = -1;

    this.searchAction.emit({
      action: 'search',
      query: this.searchQuery,
      entityType: this.selectedEntityType
    });

    this.searchCriteriaChange.emit({
      query: this.searchQuery,
      entityType: this.selectedEntityType
    });
  }

  selectSuggestion(suggestion: SearchSuggestion): void {
    this.searchQuery = suggestion.text;
    this.showDropdown = false;
    this.highlightedIndex = -1;

    this.searchAction.emit({
      action: 'select-suggestion',
      query: suggestion.text,
      suggestion
    });
  }

  selectRecentSearch(recent: RecentSearch): void {
    this.searchQuery = recent.query;
    this.selectedEntityType = recent.entityType;
    this.showDropdown = false;
    this.highlightedIndex = -1;

    this.searchAction.emit({
      action: 'select-recent',
      query: recent.query,
      entityType: recent.entityType,
      recentSearch: recent
    });

    this.searchCriteriaChange.emit({
      query: recent.query,
      entityType: recent.entityType
    });
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.suggestions = [];
    this.highlightedIndex = -1;
    this.showDropdown = true;
    this.loadRecentSearches();
  }

  async clearRecentSearches(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    try {
      await this.api.clearRecentSearches().toPromise();
      this.recentSearches = [];
      this.searchAction.emit({ action: 'clear-recent' });
    } catch (error) {
      console.error('Failed to clear recent searches', error);
    }
  }

  getEntityTypeClass(entityType: string): string {
    return entityType?.toLowerCase() || 'all';
  }

  getEntityTypeIcon(entityType: string): string {
    switch (entityType?.toLowerCase()) {
      case 'part': return 'P';
      case 'document': return 'D';
      case 'file': return 'F';
      case 'change': return 'C';
      default: return 'A';
    }
  }

  getEntityTypeLabel(entityType: SearchEntityType): string {
    switch (entityType) {
      case SearchEntityType.Part: return 'Parts';
      case SearchEntityType.Document: return 'Documents';
      case SearchEntityType.File: return 'Files';
      case SearchEntityType.Change: return 'Changes';
      default: return 'All';
    }
  }

  /** Public method to focus the search input */
  focus(): void {
    this.searchInputElement?.nativeElement?.focus();
  }

  /** Public method to set search query programmatically */
  setQuery(query: string, entityType?: SearchEntityType): void {
    this.searchQuery = query;
    if (entityType !== undefined) {
      this.selectedEntityType = entityType;
    }
  }
}
